import React, { useState, useEffect, useRef } from "react";
import {
  MdStar,
  MdStarBorder,
  MdCheck,
  MdDarkMode,
  MdLightMode,
} from "react-icons/md";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/solid";
import { motion, AnimatePresence } from "framer-motion";
import { FaRegHandshake } from "react-icons/fa6";
import { Info, Video, X } from "lucide-react";
import { MdAddAPhoto } from "react-icons/md";
import { MdShield } from "react-icons/md";

import axios from "axios";
import { baseUrl } from "../../Environments/environment";
import Navbar from "../Navbar/navbar";
import RatingLoader from "./RatingLoader";
import { getReviewConfig } from "../../configs/reviewConfigs";

const getInitials = (firstName, lastName) => {
  if (!firstName && !lastName) return "?";
  return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
};

const PropertyRatingDisplay = ({
  guest: propGuest,
  propertyId,
  property,
  review,
  initialRating: propInitialRating,
  onComplete, // Callback when review is submitted
  onCancel, // Callback when user cancels
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [propertyMobile, setPropertyMobile] = useState(null);
  const [propertyLogo, setPropertyLogo] = useState(null);
  const [propertyName, setPropertyName] = useState(null);

  // Use props if available, otherwise fallback to location.state
  const { guest, initialRating } = location.state || {
    guest: propGuest,
    initialRating: propInitialRating,
  };

  const [businessType, setBusinessType] = useState(""); // Business type fetched from API

  const [ratings, setRatings] = useState({});
  const [details, setDetails] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [photos, setPhotos] = useState([]);

  // Info popup state
  const [infoOpen, setInfoOpen] = useState(false);
  const infoRef = useRef(null);
  const popupRef = useRef(null);

  // Selection state for cards
  const [selectedTripType, setSelectedTripType] = useState(null);
  const [selectedTravelWith, setSelectedTravelWith] = useState(null);
  const [selectedHotelDesc, setSelectedHotelDesc] = useState([]);

  // Accordion state for details card
  const [openDetail, setOpenDetail] = useState([]);
  const [detailsText, setDetailsText] = useState({});

  // Light/Dark mode toggle state
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Close popup on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        infoRef.current &&
        !infoRef.current.contains(event.target) &&
        popupRef.current &&
        !popupRef.current.contains(event.target)
      ) {
        setInfoOpen(false);
      }
    }
    if (infoOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [infoOpen]);

  // Initialize ratings based on business type
  useEffect(() => {
    if (businessType) {
      const config = getReviewConfig(businessType);
      const initialRatings = {};
      config.ratingCategories.forEach((category) => {
        // Set overAllRating from initialRating prop if available
        if (category.key === "overAllRating" && initialRating) {
          initialRatings[category.key] = initialRating;
        } else {
          initialRatings[category.key] = 0;
        }
      });
      setRatings(initialRatings);

      // Initialize detail sections
      const initialDetails = {};
      config.detailSections.forEach((section) => {
        initialDetails[section] = "";
      });
      setDetailsText(initialDetails);
    }
  }, [businessType, initialRating]);

  useEffect(() => {
    getReview();
    getGuestPropertyData();
  }, []);

  function handleAddPhotos(e) {
    if (!e.target.files) return;
    if (photos.find((p) => p.name === e.target.files[0].name)) return;
    setPhotos([...photos, ...Array.from(e.target.files)]);
  }
  console.log(photos);
  const getReview = async () => {
    try {
      // const response = await axios.get(
      //   `${baseUrl}/guestApi/${guestSessionId}/review`
      // );
      if (review && businessType) {
        const config = getReviewConfig(businessType);
        const savedRatings = {};
        config.ratingCategories.forEach((category) => {
          savedRatings[category.key] = review[category.key] || 0;
        });
        setRatings(savedRatings);
      }
      setDetails(review.comments || "");
    } catch (error) {
      console.error("Error submitting data:", error);
    }
  };

  const getGuestPropertyData = async () => {
    try {
      // const response = await axios.get(
      //   `${baseUrl}/guestApi/${guestSessionId}/property`
      // );
      const { countryCode, phoneNumber } = property.guestPropertyMobile;
      setPropertyMobile(countryCode + phoneNumber);
      setPropertyLogo(property.property.logoUrl);
      setPropertyName(property.property.name);

      // TODO: Uncomment this once backend returns businessType
      const businessTypeFromApi = property.property.businessType || "hotel";
      setBusinessType(businessTypeFromApi);
    } catch (error) {
      console.error("Error fetching guest data:", error);
    }
  };
  async function handleSubmiReview(rate) {
    try {
      setIsLoading(true);
      setShowLoader(true);
      const formData = new FormData();

      // Add photos
      photos.forEach((photo, index) => {
        formData.append("photos", photo);
      });

      formData.append("propertyId", propertyId);
      formData.append("businessType", businessType);

      // Send the initial rating that triggered the internal review
      if (initialRating) {
        formData.append("initialRating", initialRating);
      }

      // Dynamically append rating fields based on business type
      if (businessType) {
        const config = getReviewConfig(businessType);
        config.ratingCategories.forEach((category) => {
          formData.append(category.key, ratings[category.key] || 0);
        });
      }

      if (details) formData.append("comments", details);
      if (selectedTripType) formData.append("tripType", selectedTripType);
      if (selectedTravelWith) formData.append("travelWith", selectedTravelWith);

      // FIX: Change from description[index] to hotelDescription
      selectedHotelDesc.forEach((desc) => {
        formData.append("hotelDescription[]", desc);
      });

      formData.append("detailedFeedback", JSON.stringify(detailsText));

      const res = await axios.post(
        `${baseUrl}/guestApi/property/${propertyId}/guest`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (res.status === 201) {
        const id = res.data?.result?.id;
        navigate(`/${id}`);
      }

      setIsLoading(false);

      // If onComplete callback is provided, call it instead of navigate
      if (onComplete) {
        // Wait a bit for the loader animation to finish
        setTimeout(() => {
          setShowLoader(false);
          onComplete();
        }, 2000);
      }
      // Otherwise, use default navigation (for when accessed via route)
      // The RatingLoader component will handle navigation after animation
    } catch (error) {
      setIsLoading(false);
      setShowLoader(false);
      console.error("Error submitting data:", error);
      console.error("Backend error message:", error.response?.data);
    }
  }

  // async function handleSubmiReview(rate) {
  //   try {
  //     setIsLoading(true);
  //     setShowLoader(true);
  // 		const formData = new FormData();

  // 		// Add photos
  // 		photos.forEach((photo, index) => {
  // 			formData.append("photos", photo);
  // 		});

  // 		formData.append("guestSessionId", guestSessionId);
  // 		formData.append("businessType", businessType);

  // 		// Dynamically append rating fields based on business type
  // 		if (businessType && REVIEW_CONFIGS[businessType]) {
  // 			const config = REVIEW_CONFIGS[businessType];
  // 			config.ratingCategories.forEach(category => {
  // 				formData.append(category.key, ratings[category.key] || 0);
  // 			});
  // 		}

  // 		if (details) formData.append("comments", details);
  // 		if (selectedTripType) formData.append("tripType", selectedTripType);
  // 		if (selectedTravelWith) formData.append("travelWith", selectedTravelWith);
  // 		selectedHotelDesc.forEach((desc, index) => {
  // 			formData.append(`description[${index}]`, desc);
  // 		});
  // 		formData.append("detailedFeedback", JSON.stringify(detailsText));

  //     const response = await axios.patch(
  //       `${baseUrl}/guestApi/${guestSessionId}/complete-review`,
  // 			formData,
  // 			{
  // 				headers: {
  // 					"Content-Type": "multipart/form-data",
  // 				},
  // 			},
  //     );
  //     setIsLoading(false);

  //   } catch (error) {
  //     setIsLoading(false);
  //     setShowLoader(false);
  //     console.error("Error submitting data:", error);
  //     console.error("Backend error message:", error.response?.data);
  //   }
  // }

  const handleStarClick = (category, newRating) => {
    setRatings((prev) => ({
      ...prev,
      [category]: newRating,
    }));
  };

  const renderStars = (category, rating) => {
    return Array(5)
      .fill(0)
      .map((_, index) => (
        <motion.button
          key={index}
          onClick={() => handleStarClick(category, index + 1)}
          className="transition-all duration-200 p-1"
          whileHover={{
            scale: 1.1,
            filter: "drop-shadow(0 2px 6px #4F46E533)",
          }}
          whileTap={{ scale: 0.95 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
        >
          <MdStar
            size={window.innerWidth <= 768 ? 32 : 24}
            className={`${
              index < rating
                ? "fill-[#4F46E5] text-[#4F46E5]"
                : "text-[#4F46E5]"
            } transition-colors duration-200 hover:text-[#4F46E5]`}
          />
        </motion.button>
      ));
  };

  // Get current config based on business type
  const currentConfig = getReviewConfig(businessType);

  const isPostEnabled = ratings["overAllRating"] > 0;

  return (
    <>
      {/* Rating Loader */}
      {showLoader && (
        <RatingLoader
          isApiComplete={!isLoading}
          onComplete={() => {
            setShowLoader(false);
            // Use onComplete callback if provided, otherwise navigate
            if (onComplete) {
              onComplete();
            } else {
              navigate(`/${propertyId}`);
            }
          }}
        />
      )}

      {/* Fixed Custom Header */}
      <div
        className={`fixed top-0 left-0 w-full z-50 shadow-sm h-14 flex items-center justify-center
        ${isDarkMode ? "bg-[#1F1F1F]" : "bg-white"}`}
      >
        {/* <button
          type="button"
          aria-label="Toggle dark mode"
          className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-transparent focus:outline-none"
          onClick={() => setIsDarkMode((v) => !v)}
        >
          <AnimatePresence mode="wait" initial={false}>
            {isDarkMode ? (
              <motion.span
                key="moon"
                initial={{ rotate: 90, opacity: 0, scale: 0.7 }}
                animate={{ rotate: 0, opacity: 1, scale: 1 }}
                exit={{ rotate: -90, opacity: 0, scale: 0.7 }}
								transition={{ type: "spring", stiffness: 400, damping: 30 }}
                className="block"
              >
								<MdDarkMode
									className={`w-5 h-5 ${isDarkMode ? "text-white" : "text-gray-500"}`}
								/>
              </motion.span>
            ) : (
              <motion.span
                key="sun"
                initial={{ rotate: -90, opacity: 0, scale: 0.7 }}
                animate={{ rotate: 0, opacity: 1, scale: 1 }}
                exit={{ rotate: 90, opacity: 0, scale: 0.7 }}
								transition={{ type: "spring", stiffness: 400, damping: 30 }}
                className="block"
              >
                <MdLightMode className="w-5 h-5 text-gray-700" />
              </motion.span>
            )}
          </AnimatePresence>
				</button> */}
        <button
          type="button"
          aria-label="Go back"
          onClick={() => onCancel()}
          className={`absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full focus:outline-none focus:ring-2
            ${
              isDarkMode
                ? "hover:bg-[#232323] focus:ring-[#232323]"
                : "hover:bg-gray-100 focus:ring-gray-300"
            }`}
        >
          <ArrowLeftIcon
            className={`w-5 h-5 ${isDarkMode ? "text-white" : "text-gray-500"}`}
          />
        </button>
        <span className="text-base font-normal text-gray-800 truncate px-4">
          <span className={isDarkMode ? "text-white" : "text-gray-800"}>
            {propertyName || "Property Name"}
          </span>
        </span>
      </div>
      <div
        className={`min-h-screen flex flex-col items-center pt-14 ${
          isDarkMode ? "bg-[#1F1F1F]" : "bg-slate-50"
        }`}
      >
        {/* {guest && (
          <Navbar
            first={guest.firstName}
            last={guest.lastName}
            propertyMobile={propertyMobile}
            propertyLogo={propertyLogo}
          />
        )} */}
        <div className="w-full max-w-md mx-auto flex flex-col items-start px-4 sm:px-0 h-[calc(100vh-56px)] overflow-y-auto pb-24 pt-6">
          {/* Profile Section - left aligned */}
          <div className="flex items-center w-full mb-6 gap-3 relative">
            {/* Empty space for alignment, no accent bar */}
            <div className="w-2" />
            <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-base font-bold text-gray-700 border border-gray-200">
              {getInitials(guest?.firstName, guest?.lastName)}
            </div>
            <div className="flex flex-col justify-center">
              <div
                className={`text-sm font-normal ${
                  isDarkMode ? "text-white" : "text-gray-800"
                }`}
              >
                {(() => {
                  const firstName = guest?.firstName;
                  const lastName = guest?.lastName;
                  const phoneNumber = guest?.phoneNumber;

                  // If we have at least one name, display the name
                  if (firstName || lastName) {
                    return `${firstName || ""} ${lastName || ""}`.trim();
                  }

                  // Otherwise, display phone number
                  return phoneNumber;
                })()}
              </div>
              <div
                className={`text-xs mt-0.5 flex items-center gap-1 ${
                  isDarkMode ? "text-[#BDC1C6]" : "text-gray-400"
                }`}
              >
                Posting publicly across Onelyk.
                <button
                  ref={infoRef}
                  type="button"
                  aria-label="Info"
                  className="focus:outline-none"
                  onClick={() => setInfoOpen((v) => !v)}
                >
                  <Info
                    size={16}
                    className={isDarkMode ? "text-[#BDC1C6]" : "text-gray-400"}
                  />
                </button>
              </div>
            </div>
            {/* Info Popup */}
            {infoOpen && (
              <div
                ref={popupRef}
                className={`absolute z-50 w-56 max-w-xs rounded-lg shadow-lg p-4 text-xs animate-fade-in left-1/2 -translate-x-1/2 mt-2
                  ${
                    isDarkMode
                      ? "bg-[#232323] border border-gray-600 text-gray-200"
                      : "bg-white border border-gray-200 text-gray-700"
                  }`}
                style={{ minHeight: 180, top: "100%" }}
              >
                <div className="max-h-64 overflow-y-auto pr-1 scrollbar-hide">
                  <div className="text-sm leading-relaxed space-y-3">
                    <div>
                      <strong>1. Internal Use of Reviews</strong>
                      <p className="mt-1">
                        By submitting your review through our guest engagement
                        system:
                      </p>
                      <ul className="list-disc list-inside mt-1 space-y-1 ml-2">
                        <li>
                          You agree that your feedback may be shared internally
                          with hotel management and staff to help improve
                          service quality.
                        </li>
                        <li>
                          Your review may be stored and analyzed within the
                          Onelyk platform for operational insights and service
                          improvement.
                        </li>
                        <li>
                          We may use anonymized data from your review (e.g.,
                          satisfaction trends, keywords) for reporting and
                          performance tracking.
                        </li>
                      </ul>
                    </div>

                    <div>
                      <strong>2. No Public Display or Marketing</strong>
                      <ul className="list-disc list-inside mt-1 space-y-1 ml-2">
                        <li>
                          Your review will not be publicly posted or shared on
                          any external websites (e.g., Google, TripAdvisor,
                          Yelp) without your explicit permission.
                        </li>
                        <li>
                          Your name, contact information, or personal details
                          will not be shared externally or used for marketing
                          purposes.
                        </li>
                      </ul>
                    </div>

                    <div>
                      <strong>3. Data Security</strong>
                      <ul className="list-disc list-inside mt-1 space-y-1 ml-2">
                        <li>
                          All feedback is stored securely in compliance with
                          data protection regulations.
                        </li>
                        <li>
                          Only authorized personnel at the hotel and Onelyk
                          support team will have access to your review data.
                        </li>
                      </ul>
                    </div>

                    <div>
                      <strong>4. Your Rights</strong>
                      <ul className="list-disc list-inside mt-1 space-y-1 ml-2">
                        <li>
                          If you wish to have your review removed or updated
                          from our internal system, you may contact the hotel
                          directly or email Onelyk support at{" "}
                          <span className="text-blue-400">
                            support@onelyk.com
                          </span>
                          .
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* Ratings Section - no card, direct on background, minimal spacing */}
          <div className="w-full flex flex-col gap-0 mb-2">
            {/* Centered overall rating stars */}
            <div className="flex justify-center pt-2 pb-2">
              {Array(5)
                .fill(0)
                .map((_, index) => (
                  <motion.button
                    key={index}
                    onClick={() => handleStarClick("overAllRating", index + 1)}
                    className="transition-all duration-200 p-1"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    disabled={initialRating > 0}
                  >
                    {index < (ratings["overAllRating"] || 0) ? (
                      <MdStar size={36} className="text-[#FABB05]" />
                    ) : (
                      <MdStarBorder
                        size={36}
                        className={
                          isDarkMode ? "text-gray-600" : "text-gray-400"
                        }
                      />
                    )}
                  </motion.button>
                ))}
            </div>
            {/* Other categories */}
            {currentConfig.ratingCategories
              .filter((c) => c.key !== "overAllRating")
              .map((category) => (
                <div
                  key={category.key}
                  className="flex items-center justify-between py-2"
                >
                  <div
                    className={`text-base font-normal ${
                      isDarkMode ? "text-white" : "text-gray-800"
                    }`}
                  >
                    {category.label}
                  </div>
                  <div className="flex gap-1">
                    {Array(5)
                      .fill(0)
                      .map((_, index) => (
                        <motion.button
                          key={index}
                          onClick={() =>
                            handleStarClick(category.key, index + 1)
                          }
                          className="transition-all duration-200 p-0.5"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                          transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 20,
                          }}
                        >
                          {index < (ratings[category.key] || 0) ? (
                            <MdStar size={28} className="text-[#FABB05]" />
                          ) : (
                            <MdStarBorder
                              size={28}
                              className={
                                isDarkMode ? "text-gray-600" : "text-gray-400"
                              }
                            />
                          )}
                        </motion.button>
                      ))}
                  </div>
                </div>
              ))}
          </div>
          {/* Comment Box - no card, direct on background, minimal spacing */}
          <div className="w-full mb-4">
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value.slice(0, 500))}
              placeholder={currentConfig.commentPlaceholder}
              className={`w-full border rounded-lg p-4 text-sm resize-none min-h-[100px] shadow-sm
                ${
                  isDarkMode
                    ? "bg-[#232323] border-gray-600 text-white placeholder:text-gray-400 focus:ring-2 focus:ring-[#8AB4F8]"
                    : "bg-white border-gray-200 text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:bg-white"
                }
                transition-all duration-200`}
              rows={4}
            />
          </div>
          {/* Add Photos & Videos Button */}
          <div className="w-full flex justify-end mb-4">
            <input
              type="file"
              id="photoUpload"
              accept="image/*,video/*"
              multiple
              className="hidden"
              onChange={handleAddPhotos}
            />
            <label htmlFor="photoUpload" className="w-full">
              <div
                className={`w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg border font-normal transition relative cursor-pointer
        ${
          isDarkMode
            ? "border-gray-500 text-[#8AB4F8] bg-[#232323] hover:bg-gray-800"
            : "border-gray-300 text-gray-600 bg-white hover:bg-gray-50"
        }`}
              >
                <MdAddAPhoto className="w-5 h-5" />
                Add Photos & Videos
              </div>
            </label>
          </div>
          {/* Display selected photos as thumbnails */}
          {photos.length > 0 && (
            <div className="w-full mb-4">
              <div className="flex flex-wrap gap-2">
                {photos.map((photo, index) => {
                  const objectUrl = URL.createObjectURL(photo);
                  return (
                    <div
                      key={index}
                      className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200"
                    >
                      {photo.type.startsWith("image/") ? (
                        <img
                          src={objectUrl}
                          alt={`Upload ${index + 1}`}
                          className="w-full h-full object-cover"
                          onLoad={() => URL.revokeObjectURL(objectUrl)} // Free memory
                        />
                      ) : (
                        // biome-ignore lint/a11y/useMediaCaption: <no caption required>
                        <>
                          <Video className="absolute top-1 left-1 w-6 h-6 text-white bg-black bg-opacity-50 rounded-full p-1 z-10" />
                          <video
                            autoPlay={false}
                            muted
                            className="w-full h-full object-cover"
                          >
                            <source src={objectUrl} type={photo.type} />
                          </video>
                        </>
                      )}
                      )
                      <button
                        type="button"
                        onClick={() => {
                          setPhotos(photos.filter((_, i) => i !== index));
                          URL.revokeObjectURL(objectUrl); // Free memory
                        }}
                        className="absolute top-1 right-1 bg-black bg-opacity-50 text-white rounded-full p-1 hover:bg-opacity-75 focus:outline-none"
                      >
                        {" "}
                        <X className="w-4 h-4" />{" "}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Custom Cards Section */}
          {(currentConfig.descriptionOptions.length > 0 ||
            currentConfig.travelWithOptions.length > 0 ||
            currentConfig.tripTypeOptions.length > 0) && (
            <div className="w-full flex flex-col gap-5 mb-8">
              {/* Check if beautySalon business type to render cards in different order */}
              {[
                "beauty_salon",
                "nail_salon",
                "hair_salon",
                "spa",
                "massage_spa",
                "barber_shop",
                "tattoo_shop",
              ].includes(businessType) ? (
                <>
                  {/* For Beauty Salons: Description → TravelWith */}
                  {/* Card 1: Description (renders first for beauty salons) */}
                  {currentConfig.descriptionOptions.length > 0 && (
                    <div
                      className={`rounded-xl border p-4 shadow-md
                  ${
                    isDarkMode
                      ? "bg-[#242832] border-[#242832]"
                      : "bg-white border-gray-100"
                  }`}
                    >
                      <div
                        className={`font-normal mb-1 ${
                          isDarkMode ? "text-white" : "text-gray-800"
                        }`}
                      >
                        {currentConfig.descriptionQuestion}
                      </div>
                      <div
                        className={`text-xs mb-2 ${
                          isDarkMode ? "text-gray-400" : "text-gray-400"
                        }`}
                      >
                        (Select all that apply)
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {currentConfig.descriptionOptions.map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() =>
                              setSelectedHotelDesc(
                                selectedHotelDesc.includes(option)
                                  ? selectedHotelDesc.filter(
                                      (item) => item !== option
                                    )
                                  : [...selectedHotelDesc, option]
                              )
                            }
                            className={`px-4 py-2 rounded-lg border text-sm font-normal transition flex items-center gap-1
                          ${
                            isDarkMode
                              ? selectedHotelDesc.includes(option)
                                ? "bg-[#3A3F4D] text-gray-200 border-gray-500"
                                : "bg-[#242832] text-[#9AA0A6] border-gray-600 hover:bg-[#3A3F4D]"
                              : selectedHotelDesc.includes(option)
                              ? "bg-[#4F46E5] text-white border-[#4F46E5]"
                              : "border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100"
                          }`}
                          >
                            {selectedHotelDesc.includes(option) && (
                              <motion.span
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0, opacity: 0 }}
                                transition={{
                                  type: "spring",
                                  stiffness: 400,
                                  damping: 20,
                                }}
                              >
                                <MdCheck className="w-4 h-4 mr-1" />
                              </motion.span>
                            )}
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Card 2: TravelWith (renders second for beauty salons) */}
                  {currentConfig.travelWithOptions.length > 0 && (
                    <div
                      className={`rounded-xl border p-4 shadow-md
                  ${
                    isDarkMode
                      ? "bg-[#242832] border-[#242832]"
                      : "bg-white border-gray-100"
                  }`}
                    >
                      <div
                        className={`font-normal mb-3 ${
                          isDarkMode ? "text-white" : "text-gray-800"
                        }`}
                      >
                        {currentConfig.travelWithQuestion}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {currentConfig.travelWithOptions.map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() =>
                              setSelectedTravelWith(
                                selectedTravelWith === option ? null : option
                              )
                            }
                            className={`px-4 py-2 rounded-lg border text-sm font-normal transition flex items-center gap-1
                          ${
                            isDarkMode
                              ? selectedTravelWith === option
                                ? "bg-[#3A3F4D] text-gray-200 border-gray-500"
                                : "bg-[#242832] text-[#9AA0A6] border-gray-600 hover:bg-[#3A3F4D]"
                              : selectedTravelWith === option
                              ? "bg-[#4F46E5] text-white border-[#4F46E5]"
                              : "border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100"
                          }`}
                          >
                            {selectedTravelWith === option && (
                              <motion.span
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0, opacity: 0 }}
                                transition={{
                                  type: "spring",
                                  stiffness: 400,
                                  damping: 20,
                                }}
                              >
                                <MdCheck className="w-4 h-4 mr-1" />
                              </motion.span>
                            )}
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : [
                  "hvac_contractor",
                  "plumber",
                  "electrician",
                  "roofing_contractor",
                  "general_contractor",
                  "foundation_repair_service",
                  "fence_contractor",
                  "water_damage_restoration_service",
                  "pest_control_service",
                  "garage_door_supplier",
                  "solar_energy_contractor",
                  "pool_cleaning_service",
                  "locksmith",
                  "painting_contractor",
                  "landscaper",
                  "lawn_care_service",
                  "gutter_cleaning_service",
                ].includes(businessType) ? (
                <>
                  {/* For Home Services: Description → TripType → TravelWith */}
                  {/* Card 1: Description (renders first for home services) */}
                  {currentConfig.descriptionOptions.length > 0 && (
                    <div
                      className={`rounded-xl border p-4 shadow-md
                  ${
                    isDarkMode
                      ? "bg-[#242832] border-[#242832]"
                      : "bg-white border-gray-100"
                  }`}
                    >
                      <div
                        className={`font-normal mb-1 ${
                          isDarkMode ? "text-white" : "text-gray-800"
                        }`}
                      >
                        {currentConfig.descriptionQuestion}
                      </div>
                      <div
                        className={`text-xs mb-2 ${
                          isDarkMode ? "text-gray-400" : "text-gray-400"
                        }`}
                      >
                        (Select all that apply)
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {currentConfig.descriptionOptions.map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() =>
                              setSelectedHotelDesc(
                                selectedHotelDesc.includes(option)
                                  ? selectedHotelDesc.filter(
                                      (item) => item !== option
                                    )
                                  : [...selectedHotelDesc, option]
                              )
                            }
                            className={`px-4 py-2 rounded-lg border text-sm font-normal transition flex items-center gap-1
                          ${
                            isDarkMode
                              ? selectedHotelDesc.includes(option)
                                ? "bg-[#3A3F4D] text-gray-200 border-gray-500"
                                : "bg-[#242832] text-[#9AA0A6] border-gray-600 hover:bg-[#3A3F4D]"
                              : selectedHotelDesc.includes(option)
                              ? "bg-[#4F46E5] text-white border-[#4F46E5]"
                              : "border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100"
                          }`}
                          >
                            {selectedHotelDesc.includes(option) && (
                              <motion.span
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0, opacity: 0 }}
                                transition={{
                                  type: "spring",
                                  stiffness: 400,
                                  damping: 20,
                                }}
                              >
                                <MdCheck className="w-4 h-4 mr-1" />
                              </motion.span>
                            )}
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Card 2: TripType (renders second for home services) */}
                  {currentConfig.tripTypeOptions.length > 0 && (
                    <div
                      className={`rounded-xl border p-4 shadow-md
                  ${
                    isDarkMode
                      ? "bg-[#242832] border-[#242832]"
                      : "bg-white border-gray-100"
                  }`}
                    >
                      <div
                        className={`font-normal mb-3 ${
                          isDarkMode ? "text-white" : "text-gray-800"
                        }`}
                      >
                        {currentConfig.tripTypeQuestion}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {currentConfig.tripTypeOptions.map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() =>
                              setSelectedTripType(
                                selectedTripType === option ? null : option
                              )
                            }
                            className={`px-4 py-2 rounded-lg border text-sm font-normal transition flex items-center gap-1
                          ${
                            isDarkMode
                              ? selectedTripType === option
                                ? "bg-[#3A3F4D] text-gray-200 border-gray-500"
                                : "bg-[#242832] text-[#9AA0A6] border-gray-600 hover:bg-[#3A3F4D]"
                              : selectedTripType === option
                              ? "bg-[#4F46E5] text-white border-[#4F46E5]"
                              : "border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100"
                          }`}
                          >
                            {selectedTripType === option && (
                              <motion.span
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0, opacity: 0 }}
                                transition={{
                                  type: "spring",
                                  stiffness: 400,
                                  damping: 20,
                                }}
                              >
                                <MdCheck className="w-4 h-4 mr-1" />
                              </motion.span>
                            )}
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Card 3: TravelWith (renders third for home services) */}
                  {currentConfig.travelWithOptions.length > 0 && (
                    <div
                      className={`rounded-xl border p-4 shadow-md
                  ${
                    isDarkMode
                      ? "bg-[#242832] border-[#242832]"
                      : "bg-white border-gray-100"
                  }`}
                    >
                      <div
                        className={`font-normal mb-3 ${
                          isDarkMode ? "text-white" : "text-gray-800"
                        }`}
                      >
                        {currentConfig.travelWithQuestion}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {currentConfig.travelWithOptions.map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() =>
                              setSelectedTravelWith(
                                selectedTravelWith === option ? null : option
                              )
                            }
                            className={`px-4 py-2 rounded-lg border text-sm font-normal transition flex items-center gap-1
                          ${
                            isDarkMode
                              ? selectedTravelWith === option
                                ? "bg-[#3A3F4D] text-gray-200 border-gray-500"
                                : "bg-[#242832] text-[#9AA0A6] border-gray-600 hover:bg-[#3A3F4D]"
                              : selectedTravelWith === option
                              ? "bg-[#4F46E5] text-white border-[#4F46E5]"
                              : "border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100"
                          }`}
                          >
                            {selectedTravelWith === option && (
                              <motion.span
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0, opacity: 0 }}
                                transition={{
                                  type: "spring",
                                  stiffness: 400,
                                  damping: 20,
                                }}
                              >
                                <MdCheck className="w-4 h-4 mr-1" />
                              </motion.span>
                            )}
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* For All Other Business Types: TripType → TravelWith → Description */}
                  {/* Card 1: TripType (original order) */}
                  {currentConfig.tripTypeOptions.length > 0 && (
                    <div
                      className={`rounded-xl border p-4 shadow-md
                  ${
                    isDarkMode
                      ? "bg-[#242832] border-[#242832]"
                      : "bg-white border-gray-100"
                  }`}
                    >
                      <div
                        className={`font-normal mb-3 ${
                          isDarkMode ? "text-white" : "text-gray-800"
                        }`}
                      >
                        {currentConfig.tripTypeQuestion}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {currentConfig.tripTypeOptions.map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() =>
                              setSelectedTripType(
                                selectedTripType === option ? null : option
                              )
                            }
                            className={`px-4 py-2 rounded-lg border text-sm font-normal transition flex items-center gap-1
                          ${
                            isDarkMode
                              ? selectedTripType === option
                                ? "bg-[#3A3F4D] text-gray-200 border-gray-500"
                                : "bg-[#242832] text-[#9AA0A6] border-gray-600 hover:bg-[#3A3F4D]"
                              : selectedTripType === option
                              ? "bg-[#4F46E5] text-white border-[#4F46E5]"
                              : "border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100"
                          }`}
                          >
                            {selectedTripType === option && (
                              <motion.span
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0, opacity: 0 }}
                                transition={{
                                  type: "spring",
                                  stiffness: 400,
                                  damping: 20,
                                }}
                              >
                                <MdCheck className="w-4 h-4 mr-1" />
                              </motion.span>
                            )}
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Card 2: TravelWith (original order) */}
                  {currentConfig.travelWithOptions.length > 0 && (
                    <div
                      className={`rounded-xl border p-4 shadow-md
                  ${
                    isDarkMode
                      ? "bg-[#242832] border-[#242832]"
                      : "bg-white border-gray-100"
                  }`}
                    >
                      <div
                        className={`font-normal mb-3 ${
                          isDarkMode ? "text-white" : "text-gray-800"
                        }`}
                      >
                        {currentConfig.travelWithQuestion}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {currentConfig.travelWithOptions.map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() =>
                              setSelectedTravelWith(
                                selectedTravelWith === option ? null : option
                              )
                            }
                            className={`px-4 py-2 rounded-lg border text-sm font-normal transition flex items-center gap-1
                          ${
                            isDarkMode
                              ? selectedTravelWith === option
                                ? "bg-[#3A3F4D] text-gray-200 border-gray-500"
                                : "bg-[#242832] text-[#9AA0A6] border-gray-600 hover:bg-[#3A3F4D]"
                              : selectedTravelWith === option
                              ? "bg-[#4F46E5] text-white border-[#4F46E5]"
                              : "border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100"
                          }`}
                          >
                            {selectedTravelWith === option && (
                              <motion.span
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0, opacity: 0 }}
                                transition={{
                                  type: "spring",
                                  stiffness: 400,
                                  damping: 20,
                                }}
                              >
                                <MdCheck className="w-4 h-4 mr-1" />
                              </motion.span>
                            )}
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Card 3: Description (original order) */}
                  {currentConfig.descriptionOptions.length > 0 && (
                    <div
                      className={`rounded-xl border p-4 shadow-md
                  ${
                    isDarkMode
                      ? "bg-[#242832] border-[#242832]"
                      : "bg-white border-gray-100"
                  }`}
                    >
                      <div
                        className={`font-normal mb-1 ${
                          isDarkMode ? "text-white" : "text-gray-800"
                        }`}
                      >
                        {currentConfig.descriptionQuestion}
                      </div>
                      <div
                        className={`text-xs mb-2 ${
                          isDarkMode ? "text-gray-400" : "text-gray-400"
                        }`}
                      >
                        (Select all that apply)
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {currentConfig.descriptionOptions.map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() =>
                              setSelectedHotelDesc(
                                selectedHotelDesc.includes(option)
                                  ? selectedHotelDesc.filter(
                                      (item) => item !== option
                                    )
                                  : [...selectedHotelDesc, option]
                              )
                            }
                            className={`px-4 py-2 rounded-lg border text-sm font-normal transition flex items-center gap-1
                          ${
                            isDarkMode
                              ? selectedHotelDesc.includes(option)
                                ? "bg-[#3A3F4D] text-gray-200 border-gray-500"
                                : "bg-[#242832] text-[#9AA0A6] border-gray-600 hover:bg-[#3A3F4D]"
                              : selectedHotelDesc.includes(option)
                              ? "bg-[#4F46E5] text-white border-[#4F46E5]"
                              : "border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100"
                          }`}
                          >
                            {selectedHotelDesc.includes(option) && (
                              <motion.span
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0, opacity: 0 }}
                                transition={{
                                  type: "spring",
                                  stiffness: 400,
                                  damping: 20,
                                }}
                              >
                                <MdCheck className="w-4 h-4 mr-1" />
                              </motion.span>
                            )}
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Heading outside the card */}
          {currentConfig.detailSections.length > 0 && (
            <>
              <div
                className={`font-normal mb-3 ${
                  isDarkMode ? "text-white" : "text-gray-800"
                }`}
              >
                Add more details to help others
              </div>

              {/* Accordion Card - all sections in one card with separators */}
              <div
                className={`rounded-xl border p-4 shadow-md w-full
            ${
              isDarkMode
                ? "bg-[#242832] border-[#242832]"
                : "bg-white border-gray-100"
            }`}
              >
                <div className="flex flex-col">
                  {currentConfig.detailSections.map((section, idx) => (
                    <React.Fragment key={section}>
                      {idx !== 0 && (
                        <div
                          className={`border-t my-1 ${
                            isDarkMode ? "border-gray-600" : "border-gray-200"
                          }`}
                        />
                      )}
                      <div>
                        <button
                          type="button"
                          className={`w-full flex items-center justify-between px-2 py-3 focus:outline-none
                        ${isDarkMode ? "text-white" : "text-gray-800"}`}
                          onClick={() =>
                            setOpenDetail(
                              openDetail.includes(section)
                                ? openDetail.filter((s) => s !== section)
                                : [...openDetail, section]
                            )
                          }
                        >
                          <span
                            className={`font-normal text-base ${
                              isDarkMode ? "text-white" : "text-gray-800"
                            }`}
                          >
                            {section}
                          </span>
                          <ChevronDownIcon
                            className={`w-5 h-5 transform transition-transform duration-200 ${
                              openDetail.includes(section) ? "rotate-180" : ""
                            } ${
                              isDarkMode ? "text-gray-300" : "text-gray-400"
                            }`}
                          />
                        </button>
                        <AnimatePresence initial={false}>
                          {openDetail.includes(section) && (
                            <motion.div
                              key="content"
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.25, ease: "easeInOut" }}
                              className="overflow-hidden"
                            >
                              <div className="px-2 pb-4">
                                <textarea
                                  value={detailsText[section]}
                                  onChange={(e) =>
                                    setDetailsText({
                                      ...detailsText,
                                      [section]: e.target.value,
                                    })
                                  }
                                  placeholder={`Tell others about ${section.toLowerCase()}`}
                                  className={`w-full border rounded-lg p-3 text-sm resize-none placeholder:text-gray-400 focus:outline-none focus:ring-2 transition-all duration-200 min-h-[80px] shadow-sm mt-2
                                ${
                                  isDarkMode
                                    ? "bg-[#232323] border-gray-600 text-white focus:ring-[#8AB4F8] focus:bg-[#232323]"
                                    : "bg-white border-gray-300 text-gray-700 focus:ring-gray-300 focus:bg-white"
                                }`}
                                  rows={3}
                                />
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </>
          )}
          {/* Action Buttons - sticky footer */}
          <div
            className={`fixed bottom-0 left-0 w-full px-4 py-3 z-40
          ${isDarkMode ? "bg-[#1F1F1F]" : "bg-white"}`}
          >
            <div className="max-w-md mx-auto flex gap-3 justify-end">
              <button
                type="button"
                className={`py-2 px-6 rounded-lg border font-normal transition
              ${
                isDarkMode
                  ? "border-gray-500 text-[#8AB4F8] bg-[#232323] hover:bg-gray-800"
                  : "border-gray-300 text-gray-600 bg-white hover:bg-gray-50"
              }`}
                onClick={() => {
                  if (onCancel) {
                    // Call cancel callback without showing thank you message
                    onCancel();
                  } else if (onComplete) {
                    // Fallback to onComplete for backward compatibility
                    onComplete();
                  } else {
                    navigate(-1);
                  }
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`py-2 px-6 rounded-lg font-normal shadow transition disabled:opacity-50 disabled:cursor-not-allowed
              ${
                isDarkMode
                  ? isPostEnabled
                    ? "bg-[#8AB4F8] text-[#232323] hover:bg-[#669DF6]"
                    : "bg-gray-700 text-gray-400"
                  : isPostEnabled
                  ? "bg-[#4F46E5] text-white hover:bg-[#4338CA]"
                  : "bg-gray-800 text-white hover:bg-gray-700"
              }`}
                onClick={() => {
                  // Post Logic for that post function
                  handleSubmiReview();
                }}
                disabled={isLoading || !isPostEnabled}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Posting...
                  </span>
                ) : (
                  "Post"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PropertyRatingDisplay;
